import React, { useState, useCallback, useRef } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useSubscription } from "@apollo/client";
// import ChatBans from "core/components/ChatBans";
import ChatHeaderWithForm from "core/components/ChatHeaderWithForm";
import ChatMessageList from "core/components/ChatMessageList";
import ChatHeaderMenu from "core/components/ChatHeaderMenu";
import Skeleton from "@mui/material/Skeleton";
import Paper from "@mui/material/Paper";
import useChatHeartbeat from "core/hooks/useChatHeartbeat";
import useChatSoundNotifications from "core/hooks/useChatSoundNotifications";
import { ChatMembersUpdatedSubscription } from "core/subscriptions";
import useWsLinkStatus from "core/hooks/useWsLinkStatus";
import useIsMobile from "arena/hooks/useIsMobile";

const PREFIX = "Chat";

const classes = {
  root: `${PREFIX}-root`,
  rootSkeleton: `${PREFIX}-rootSkeleton`,
};

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "hasTabs" && prop !== "isMobile",
})(({ theme, hasTabs }) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "1px solid",
  borderColor: "rgba(255, 255, 255, 0.30)",

  [theme.breakpoints.up("md")]: {
    borderRadius: hasTabs ? 0 : 12,
  },
  [theme.breakpoints.down("md")]: {
    borderRadius: 0,
    border: "none",
  },

  [`& .${classes.rootSkeleton}`]: {
    width: "100%",
    [theme.breakpoints.up("md")]: {
      borderRadius: hasTabs ? 0 : 12,
    },
  },
}));

const CHAT = "CHAT";
const BANNED_USERS = "BANNED_USERS";

function Chat({ content, loading, viewer, onOpenSignIn, hasTabs }) {
  const [tab, setTab] = useState(CHAT);
  const [chatMenuAnchorEl, setChatMenuAnchorEl] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [mention, setMention] = useState(null);
  const [isInputInFocus, setIsInputInFocus] = useState(false);
  const chatFormRef = useRef();
  const blurTimeoutRef = useRef(null);
  const isMobile = useIsMobile();

  const handleMention = useCallback((mentionData) => {
    setMention(mentionData);
    if (chatFormRef.current) {
      chatFormRef.current.insertMention(mentionData.userDisplayName);
    }
  }, []);

  const handleReply = useCallback((replyData) => {
    setReplyTo(replyData);
    if (chatFormRef.current) {
      chatFormRef.current.focus();
    }
  }, []);

  const handleInputFocus = useCallback(() => {
    // Отменяем отложенное схлопывание если фокус вернулся
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsInputInFocus(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    // Небольшая задержка чтобы успел сработать submit при клике на кнопку
    blurTimeoutRef.current = setTimeout(() => {
      setIsInputInFocus(false);
      blurTimeoutRef.current = null;
    }, 200);
  }, []);

  const chatRoomId = content?.chatRoom?.id;

  useChatHeartbeat(chatRoomId, viewer);

  const { online: wsConnected } = useWsLinkStatus();

  const { data: membersData } = useSubscription(
    ChatMembersUpdatedSubscription,
    {
      skip: !chatRoomId || !wsConnected,
      variables: { chatRoomId },
    }
  );

  const {
    settings: soundSettings,
    updateSettings: updateSoundSettings,
    handleNewMessage,
  } = useChatSoundNotifications(viewer?.id);

  const onCloseChatMenu = () => setChatMenuAnchorEl(null);
  const onClickChatMenuBannedUsers = () => {
    setTab(BANNED_USERS);
    setChatMenuAnchorEl(null);
  };

  if (loading) {
    return <Skeleton variant="rectangular" className={classes.rootSkeleton} />;
  }

  if (!content?.isChatEnabled) {
    return null;
  }

  if (!content?.chatRoom?.enabled) {
    return null;
  }

  const onlineMembers = membersData?.chatMembersUpdated?.members;

  const viewerIsAdmin = content.chatRoom.viewerIsAdmin;
  const onlineCount =
    membersData?.chatMembersUpdated?.onlineCount ??
    content.chatRoom.onlineCount;

  return (
    <StyledPaper
      hasTabs={hasTabs}
      isMobile={isMobile}
      sx={{
        backgroundColor: "background.default",
        height: isMobile
          ? isInputInFocus
            ? "calc(100vh - 56.25vw)"
            : 500
          : undefined,
        position: {
          xs: isInputInFocus ? "absolute" : "static",
          md: "static",
        },
        top: isInputInFocus ? 0 : undefined,
        left: isInputInFocus ? 0 : undefined,
        right: isInputInFocus ? 0 : undefined,
        transition: "all ease 300ms",
      }}
    >
      {tab === CHAT && (
        <ChatHeaderWithForm
          ref={chatFormRef}
          tab={tab}
          key={viewer ? "viewer-form" : "guest-form"}
          viewer={viewer}
          content={content}
          chatRoom={content.chatRoom}
          rightholderId={content.rightholder.id}
          onOpenSignIn={onOpenSignIn}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          mention={mention}
          onClearMention={() => setMention(null)}
          onlineCount={onlineCount}
          onlineMembers={onlineMembers}
          chatRoomId={chatRoomId}
          onMention={handleMention}
          soundSettings={soundSettings}
          onUpdateSoundSettings={updateSoundSettings}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
        />
      )}

      {/* {tab === BANNED_USERS && <ChatBans rowsPerPage={12} />} */}

      {tab === CHAT && (
        <ChatMessageList
          key={viewer ? "viewer" : "guest"}
          viewer={viewer}
          viewerIsAdmin={viewerIsAdmin}
          chatRoomId={chatRoomId}
          onReply={handleReply}
          onMention={handleMention}
          onNewMessage={handleNewMessage}
        />
      )}

      {viewerIsAdmin && (
        <ChatHeaderMenu
          anchorEl={chatMenuAnchorEl}
          open={Boolean(chatMenuAnchorEl)}
          onClose={onCloseChatMenu}
          onClickChatMenuBannedUsers={onClickChatMenuBannedUsers}
        />
      )}
    </StyledPaper>
  );
}

Chat.propTypes = {
  isMobile: PropTypes.bool,
  inPlayer: PropTypes.bool,
  loading: PropTypes.bool,
  onOpenSignIn: PropTypes.func,
  hasTabs: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isChatEnabled: PropTypes.bool,
    chatRoom: PropTypes.shape({
      id: PropTypes.string.isRequired,
      enabled: PropTypes.bool,
      viewerIsAdmin: PropTypes.bool,
      onlineCount: PropTypes.number,
    }),
    rightholder: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    widget: PropTypes.object,
  }),
  viewer: PropTypes.object,
};

export default Chat;
